import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import { LOG_SYNONYM_OPPORTUNITY } from './graphql/logSynonymOpportunity'
import { markSynonymOriginRequest } from './synonymConversion'

/**
 * Discovers synonym opportunities from MSE search events: a zero-result search followed by a
 * successful one in the same session.
 *
 * Quality gates (to avoid the noise seen in production):
 *  - Only real submits (search-bar) can SEAL a report — autocomplete keystrokes never become a
 *    suggested term.
 *  - Uses the real total_count to decide zero results (falls back to product count).
 *  - The pending failure is stored with its request_id and a TTL, so unrelated searches are not
 *    paired together.
 *  - URL/.html and too-short fragments are dropped client-side (the backend validates again).
 */

const SUBMIT_UNIT_ID = 'search-bar'
const PENDING_FAILURE_KEY = 'synonym_pending_failure'
const FAILURE_TTL_MS = 5 * 60 * 1000
const MIN_TERM_LENGTH = 3

interface PendingFailure {
    term: string
    request_id: string
    total_count: number
    unit_id: string
    ts: number
}

const isNoisyTerm = (term: string): boolean => {
    if (!term || term.length < MIN_TERM_LENGTH) return true
    if (term.includes('/') || term.includes('://') || term.includes('www.')) {
        return true
    }
    return /\.(html?|php|aspx?|jsp)(\s|$)/.test(term)
}

export const useSynonymDiscovery = (): void => {
    const router = useRouter()
    const locale = router.locale

    useEffect(() => {
        if (typeof window === 'undefined') return

        const mse = (window as any).magentoStorefrontEvents
        if (!mse) return

        // Maps searchRequestId -> phrase captured when the request was sent.
        const searchContext = new Map<string, string>()

        const triggerSynonymReport = async (variables: {
            failed_term: string
            successful_term: string
            request_id: string
            failed_request_id?: string
            failed_total_count?: number
            successful_total_count?: number
            origin: 'SUBMIT'
            store_view?: string
        }): Promise<void> => {
            try {
                await graphqlFetch({
                    query: LOG_SYNONYM_OPPORTUNITY,
                    variables,
                    locale: locale || 'es'
                })
            } catch (error) {
                console.warn('[LiveSearch][Synonyms] Failed to report:', error)
            }
        }

        const readPendingFailure = (): PendingFailure | null => {
            try {
                const raw = sessionStorage.getItem(PENDING_FAILURE_KEY)
                if (!raw) return null
                const parsed = JSON.parse(raw) as PendingFailure
                if (!parsed?.term || Date.now() - parsed.ts > FAILURE_TTL_MS) {
                    sessionStorage.removeItem(PENDING_FAILURE_KEY)
                    return null
                }
                return parsed
            } catch {
                return null
            }
        }

        const getResultUnit = (searchRequestId: string): any => {
            const ctx = mse.context.getSearchResults?.() ?? { units: [] }
            return ctx.units?.find(
                (u: any) => u.searchRequestId === searchRequestId
            )
        }

        const onSearchRequestSent = (event: any): void => {
            const searchRequestId =
                event.searchRequestId ||
                event.eventInfo?.searchRequestId ||
                event.eventInfo?.searchInputContext?.units?.[0]?.searchRequestId
            const searchInput =
                event.eventInfo?.searchInputContext ||
                mse.context.getSearchInput()
            const unit = searchInput?.units?.find(
                (u: any) => u.searchRequestId === searchRequestId
            )
            const query = unit?.phrase?.trim().toLowerCase()

            if (query && searchRequestId) {
                searchContext.set(searchRequestId, query)
            }
        }

        const onSearchResponseReceived = (event: any): void => {
            const searchRequestId =
                event.searchRequestId ||
                event.eventInfo?.searchRequestId ||
                event.eventInfo?.searchResultsContext?.units?.[0]
                    ?.searchRequestId

            const resultUnit = getResultUnit(searchRequestId)
            const products =
                resultUnit?.products ||
                event.eventInfo?.searchResultsContext?.units?.[0]?.products ||
                []
            const unitId = resultUnit?.searchUnitId
            const totalCount =
                typeof resultUnit?.totalCount === 'number'
                    ? resultUnit.totalCount
                    : products?.length || 0

            let currentTerm = searchContext.get(searchRequestId)
            if (!currentTerm) {
                const searchInput =
                    event.eventInfo?.searchInputContext ||
                    mse.context.getSearchInput()
                const unit = searchInput?.units?.find(
                    (u: any) => u.searchRequestId === searchRequestId
                )
                currentTerm = unit?.phrase?.trim().toLowerCase()
            }
            searchContext.delete(searchRequestId)

            if (!currentTerm || isNoisyTerm(currentTerm)) {
                return
            }

            // Zero results -> remember it as a candidate failed term (from any unit).
            if (totalCount === 0) {
                const failure: PendingFailure = {
                    term: currentTerm,
                    request_id: searchRequestId,
                    total_count: 0,
                    unit_id: unitId || '',
                    ts: Date.now()
                }
                sessionStorage.setItem(
                    PENDING_FAILURE_KEY,
                    JSON.stringify(failure)
                )
                return
            }

            // Success: only a real submit can seal the pairing into a suggested term.
            if (unitId !== SUBMIT_UNIT_ID) {
                return
            }

            const failure = readPendingFailure()
            if (!failure || failure.term === currentTerm) {
                return
            }

            triggerSynonymReport({
                failed_term: failure.term,
                successful_term: currentTerm,
                request_id: searchRequestId,
                failed_request_id: failure.request_id,
                failed_total_count: failure.total_count,
                successful_total_count: totalCount,
                origin: 'SUBMIT',
                store_view: locale || undefined
            })

            // Remember this request so downstream conversions can feed behavior_score.
            markSynonymOriginRequest(searchRequestId)
            sessionStorage.removeItem(PENDING_FAILURE_KEY)
        }

        mse.subscribe?.searchRequestSent?.(onSearchRequestSent)
        mse.subscribe?.searchResponseReceived?.(onSearchResponseReceived)

        return () => {
            mse.unsubscribe?.searchRequestSent?.(onSearchRequestSent)
            mse.unsubscribe?.searchResponseReceived?.(onSearchResponseReceived)
            searchContext.clear()
        }
    }, [locale])
}
